"use client"

import { useState, useRef, useEffect } from "react"
import { X } from "lucide-react"
import { detectCameras } from "../utils/mediaDetection"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import "../componentStyle/cameraTest.css"

export function CameraTest() {
  const [cameraState, setCameraState] = useState({
    availableCameras: [],
    selectedCamera: "",
    isTesting: false,
    stream: null
  })

  const videoRef = useRef(null)
  const modalRef = useRef(null)
  const timeoutRef = useRef(null)

  const loadCameras = async () => {
    try {
      const cameras = await detectCameras()
      setCameraState(prev => ({
        ...prev,
        availableCameras: cameras,
        selectedCamera: prev.selectedCamera || cameras?.[0]?.id || ""
      }))
    } catch (err) {
      toast.error("Failed to detect cameras.")
    }
  }

  const startCameraTest = async () => {
    try {
      setCameraState(prev => ({ ...prev, isTesting: true }))

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: cameraState?.selectedCamera
          ? { deviceId: { exact: cameraState.selectedCamera } }
          : true
      })

      setCameraState(prev => ({ ...prev, stream: mediaStream }))

      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream
        await videoRef.current.play()
      }
    } catch (error) {
      console.error("Camera access error:", error)
      setCameraState(prev => ({ ...prev, isTesting: false }))
      toast.error("Unable to access camera. Please check permissions.")
    }
  }

  const stopCameraTest = () => {
    if (cameraState?.stream) {
      cameraState.stream.getTracks()?.forEach(track => track.stop())
    }

    if (videoRef?.current) {
      videoRef.current.srcObject = null
    }

    setCameraState(prev => ({ ...prev, isTesting: false, stream: null }))

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleModalClick = (e) => {
    if (modalRef?.current && !modalRef.current.contains(e.target)) {
      stopCameraTest()
    }
  }

  useEffect(() => {
    loadCameras()

    const handleDeviceChange = () => loadCameras()
    navigator.mediaDevices?.addEventListener("devicechange", handleDeviceChange)

    return () => {
      stopCameraTest()
      navigator.mediaDevices?.removeEventListener("devicechange", handleDeviceChange)
    }
  }, [])

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && cameraState?.isTesting) {
        stopCameraTest()
      }
    }

    if (cameraState?.isTesting) {
      document.addEventListener("keydown", handleEscKey)
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey)
    }
  }, [cameraState?.isTesting])

  return (
    <div className="device-group">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="device-label">Camera</div>

      <div className="device-options">
        {cameraState?.availableCameras?.map((camera) => (
          <label key={camera?.id} className="radio-option">
            <input
              type="radio"
              name="camera"
              value={camera?.id}
              checked={cameraState?.selectedCamera === camera?.id}
              onChange={(e) =>
                setCameraState(prev => ({ ...prev, selectedCamera: e.target.value }))
              }
              className="radio-input"
            />
            <span className="radio-label">{camera?.label}</span>
          </label>
        ))}
      </div>

      <button onClick={startCameraTest} className="test-button">
        Test Camera
      </button>

      {cameraState?.isTesting && (
        <div className="modal-overlay-camera" onClick={handleModalClick}>
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h3>Camera Test</h3>
              <button
                className="modal-close-btn"
                onClick={stopCameraTest}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="modal-video"
              />
            </div>
            <div className="modal-footer">
              <button onClick={stopCameraTest} className="stop-video-btn">
                Stop Video Testing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}